import {
  SearchInput,
  TableCellContentRenderer,
  TableHeaderContentRenderer
} from '@/components/basic';
import {
  ConfirmOperationModal,
  groupSharedKey
} from '@/components/integrations/external-ids/IntegrationsExternalIdsList';
import { IntegrationsExternalIdsTableView } from '@/components/integrations/external-ids/IntegrationsExternalIdsTableView';
import { classNames } from '@/lib/classNames';
import { External } from '@gettactic/api';
import React, { useMemo, useState } from 'react';
import { MdDelete } from 'react-icons/md';
import { CellProps, Column, } from 'react-table';
import { Checkbox } from 'reakit';

export interface IntegrationsExternalIdsTableProps {
  loading: boolean;
  data: External[];
  setOperationalModal: React.Dispatch<ConfirmOperationModal | null>;
  invalidateQuery: () => Promise<void>;
}
type AvailableIntegrations = 'ioffice';
const integrationByType: Record<AvailableIntegrations, string> = {
  ioffice: 'iOffice',
};
type CellPropsOnIHour<T> = CellProps<External, T>;

export function IntegrationsExternalIdsTable({
  data: rawData,
  setOperationalModal,
  invalidateQuery
}: IntegrationsExternalIdsTableProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [bulk, setBulk] = useState<string[]>([]);
  const data = useMemo(() => {
    const q = query.toLowerCase().trim();
    return q.length
      ? rawData.filter((x) => x.name?.toLowerCase().includes(q))
      : rawData;
  }, [query, rawData]);
  const selectedAll = data.length ? data.length === bulk.length : false;
  const visibleSelected = data.filter(
    (x) => bulk.indexOf(groupSharedKey(x)) > -1
  );

  const setDeleteModal = (h: External | null) => {
    if (h) {
      setOperationalModal({ operation: 'delete', applyTo: [h] });
    }
  };
  const columns: Column<External>[] = [
    {
      Header() {
        return (
          <TableHeaderContentRenderer>
            <div className="px-2">
              <Checkbox
                className="rounded text-primary focus:ring-0"
                checked={selectedAll}
                onClick={() => {
                  if (!selectedAll) {
                    setBulk((x) => data.map((x) => groupSharedKey(x)));
                  } else {
                    setBulk([]);
                  }
                }}
              />
            </div>
          </TableHeaderContentRenderer>
        );
      },
      accessor: undefined,
      id: 'selected',
      Cell({ row: { original: hour } }: CellPropsOnIHour<"selected">) {
        const selected = bulk.indexOf(groupSharedKey(hour)) > -1;
        return (
          <div className="flex items-center justify-start space-x-2 px-2">
            <Checkbox
              className="rounded text-primary focus:ring-0"
              checked={selected}
              onClick={() => {
                if (selected) {
                  setBulk((x) => x.filter((s) => s !== groupSharedKey(hour)));
                } else {
                  setBulk((x) => [...x, groupSharedKey(hour)]);
                }
              }}
            />
          </div>
        );
      }
    },
    {
      Header() {
        return (
          <TableHeaderContentRenderer>
            <div className="">Id</div>
          </TableHeaderContentRenderer>
        );
      },
      accessor: 'external_id',
      Cell: ({ value }: CellPropsOnIHour<"external_id">) => <div title={value ?? undefined}>{value}</div>
    },
    {
      Header() {
        return (
          <TableHeaderContentRenderer>
            <div className="">Type</div>
          </TableHeaderContentRenderer>
        );
      },
      accessor: 'name',
      Cell: ({ value }: {
        value: string; 
      }) => <div title={value ?? undefined}>{integrationByType[value as AvailableIntegrations]}</div>
    },
    {
      Header: '',
      id: 'edit-menu-placeholder',
      accessor: undefined,
      Cell: ({ row }: CellPropsOnIHour<'edit-menu-placeholder'>) =>
        TableCellContentRenderer.EditDeleteControls({
          row,
          setDeleteModal
        })
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between gap-16">
        <SearchInput query={query} setQuery={setQuery} />
        <div className="flex flex-nowrap">
          {visibleSelected.length ? (
            <p>Selected {visibleSelected.length}</p>
          ) : null}
          <button
            disabled={bulk.length === 0}
            onClick={(ev) => {
              ev.preventDefault();
              const deleteAll: External[] = [];
              visibleSelected.forEach((x) => deleteAll.push(x));
              setOperationalModal({ operation: 'delete', applyTo: deleteAll });
            }}
            className={classNames(
              visibleSelected.length
                ? 'text-gray-600 hover:text-gray-800'
                : 'text-gray-100',
              'px-2'
            )}
          >
            <MdDelete className="h-5 w-5" />
          </button>
        </div>
      </div>
      <IntegrationsExternalIdsTableView
        columns={columns}
        list={data}
        invalidateQuery={invalidateQuery}
      />
    </div>
  );
}
